import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import { useAuthUser } from '../../hooks/useAuthUser';
import { useTheme } from '../../contexts/ThemeContext';
import { trackEvent } from '../../utils/analytics';
import logo from '../../assets/calcium-logo.svg';
import badgeLight from '../../assets/MS_Startups_Celebration_Badge_Light.png';
import badgeDark from '../../assets/MS_Startups_Celebration_Badge_Dark.png';
import firstCarouselScreenshot from '../../assets/Screenshot 2026-03-12 185348.png';
import secondCarouselScreenshot from '../../assets/Screenshot 2026-03-12 014901.png';
import thirdCarouselScreenshot from '../../assets/Screenshot 2026-03-12 002935.png';
import fourthCarouselScreenshot from '../../assets/Screenshot 2026-03-12 010907.png';
import fifthCarouselScreenshot from '../../assets/Screenshot 2026-03-11 214948.png';
import {
	MdSmartToy,
	MdRestaurant,
	MdEmojiEvents,
	MdPeople,
	MdFitnessCenter,
	MdLightMode,
	MdDarkMode,
	MdMail,
} from 'react-icons/md';
import style from './LandingPage.module.css';

const FIRST_SCREEN_IMAGE = firstCarouselScreenshot;
const SECOND_SCREEN_IMAGE = secondCarouselScreenshot;
const THIRD_SCREEN_IMAGE = thirdCarouselScreenshot;
const FOURTH_SCREEN_IMAGE = fourthCarouselScreenshot;
const FIFTH_SCREEN_IMAGE = fifthCarouselScreenshot;

const SLIDES = [
	{
		tag: 'Personalized',
		title: 'Your Plan, Built by AI',
		description:
			'Generate a personalized plan in under 60 seconds, then edit workouts, meals, and structure anytime to match your real life.',
		icon: <MdSmartToy />,
		image: FIRST_SCREEN_IMAGE,
		placeholderLabel: 'home-dashboard.png',
	},
	{
		tag: 'Workouts',
		title: '30-Minute Guided Workouts',
		description:
			'Follow clear, structured sessions with timers, set tracking, and exercise flow that keeps you moving without overthinking.',
		icon: <MdFitnessCenter />,
		image: SECOND_SCREEN_IMAGE,
		placeholderLabel: 'workout-session.png',
	},
	{
		tag: 'Nutrition',
		title: 'Meal Plans That Fit Your Life',
		description:
			'Get practical meal guidance tailored to your goals, training style, and preferences, with simple daily structure you can actually follow.',
		icon: <MdRestaurant />,
		image: THIRD_SCREEN_IMAGE,
		placeholderLabel: 'meal-plan.png',
	},
	{
		tag: 'Progress',
		title: 'Track XP, Streaks, and Momentum',
		description:
			'See your streaks, XP, rank, and routines in one place so every workout feels like visible progress, not guesswork.',
		icon: <MdEmojiEvents />,
		image: FOURTH_SCREEN_IMAGE,
		placeholderLabel: 'xp-ranks.png',
	},
	{
		tag: 'Community',
		title: 'Your Fitness Circle',
		description:
			'Share wins, follow friends, chat in real-time, and stay accountable together. Fitness is better with people.',
		icon: <MdPeople />,
		image: FIFTH_SCREEN_IMAGE,
		placeholderLabel: 'community-feed.png',
	},
];

const TESTIMONIALS = [
	{
		quote:
			'I had a full workout and meal outline in under a minute, and I actually stuck to it after a few quick edits.',
		name: 'Marcus T.',
		platform: 'App Store',
	},
	{
		quote:
			'The guided sessions with timers remove all guesswork. I train for 30 minutes and move on with my day.',
		name: 'Elena R.',
		platform: 'Reddit',
	},
	{
		quote:
			'Streaks and XP sounded gimmicky, but they kept me consistent. I hit a 14-day streak for the first time.',
		name: 'Samira L.',
		platform: 'LinkedIn',
	},
	{
		quote:
			'I thought I needed a coach to stay on track. This gave me structure fast and made workouts feel doable again.',
		name: 'Noah B.',
		platform: 'X',
	},
	{
		quote:
			'Meal suggestions match my training and what I actually cook, so I can follow the plan all week.',
		name: 'James K.',
		platform: 'X',
	},
	{
		quote:
			'My group chats were scattered before. Having one fitness feed with friends keeps me accountable every day.',
		name: 'Chris P.',
		platform: 'Reddit',
	},
	{
		quote:
			'Simple UI, clear structure, and no overwhelm. It feels built for real people with busy schedules.',
		name: 'Antonella D.',
		platform: 'LinkedIn',
	},
	{
		quote:
			'Setup took about a minute, and I started my first session immediately. Fastest onboarding I have seen in fitness apps.',
		name: 'Daniel M.',
		platform: 'Product Hunt',
	},
];

const LandingPage = () => {
	const { user, isLoaded } = useAuthUser();
	const { isDarkMode, toggleTheme } = useTheme();
	const navigate = useNavigate();
	const buildPlanPath = '/choice';
	const [activeSlide, setActiveSlide] = useState(0);
	const [imageErrors, setImageErrors] = useState({});
	const touchStartX = useRef(0);
	const touchEndX = useRef(0);
	const autoPlayRef = useRef(null);
	const msBadge = isDarkMode ? badgeDark : badgeLight;

	const handleImageError = useCallback((index) => {
		setImageErrors((prev) => ({ ...prev, [index]: true }));
	}, []);

	useEffect(() => {
		trackEvent('landing_page_view');
	}, []);

	const stopAutoPlay = useCallback(() => {
		if (autoPlayRef.current) {
			clearInterval(autoPlayRef.current);
			autoPlayRef.current = null;
		}
	}, []);

	const startAutoPlay = useCallback(() => {
		stopAutoPlay();
		autoPlayRef.current = setInterval(() => {
			setActiveSlide((prev) => (prev + 1) % SLIDES.length);
		}, 4500);
	}, [stopAutoPlay]);

	useEffect(() => {
		startAutoPlay();
		return stopAutoPlay;
	}, [startAutoPlay, stopAutoPlay]);

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === 'ArrowRight') {
				setActiveSlide((prev) => Math.min(prev + 1, SLIDES.length - 1));
				startAutoPlay();
			} else if (e.key === 'ArrowLeft') {
				setActiveSlide((prev) => Math.max(prev - 1, 0));
				startAutoPlay();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [startAutoPlay]);

	const goToSlide = (index) => {
		setActiveSlide(index);
		startAutoPlay();
	};

	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
		touchEndX.current = e.touches[0].clientX;
		stopAutoPlay();
	};

	const handleTouchMove = (e) => {
		touchEndX.current = e.touches[0].clientX;
	};

	const handleTouchEnd = () => {
		const diff = touchStartX.current - touchEndX.current;
		if (diff > 50) {
			setActiveSlide((prev) => Math.min(prev + 1, SLIDES.length - 1));
		} else if (diff < -50) {
			setActiveSlide((prev) => Math.max(prev - 1, 0));
		}
		startAutoPlay();
	};

	const handleStart = () => {
		trackEvent('landing_click_start');
		if (isLoaded && !user) {
			navigate(`/login?redirectTo=${encodeURIComponent(buildPlanPath)}`);
			return;
		}
		navigate(buildPlanPath);
	};

	return (
		<div className={style.Page}>
			<SEO
				title="Calcium — AI Fitness, Your Way"
				description="Generate a personalized fitness and nutrition plan with AI, build your own from scratch, and adapt workouts, meals, and routines anytime."
				path="/"
			/>

			<div className={style.GlowTop} aria-hidden="true" />
			<div className={style.GlowBottom} aria-hidden="true" />

			{/* ===== Navbar ===== */}
			<nav className={style.Nav}>
				<Link to="/" className={style.LogoLink}>
					<img src={logo} alt="Calcium" className={style.LogoImg} />
					<span className={style.LogoText}>Calcium</span>
				</Link>

				<div className={style.NavRight}>
					<a
						href="mailto:hello@calcium.fitness"
						className={style.NavSupportBtn}
						aria-label="Contact support"
					>
						<MdMail />
						<span>Support</span>
					</a>
					<button
						className={style.NavIconBtn}
						onClick={toggleTheme}
						aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
					>
						{isDarkMode ? <MdLightMode /> : <MdDarkMode />}
					</button>
					{user ? (
						<Link to="/" className={style.NavBtn}>Dashboard</Link>
					) : (
						<Link
							to={`/login?redirectTo=${encodeURIComponent('/')}`}
							className={style.NavBtn}
						>
							Log in
						</Link>
					)}
				</div>
			</nav>

			{/* ===== Hero ===== */}
			<section className={style.Hero}>
				<div className={style.HeroBadge}>
					<span className={style.HeroBadgeDot} />
					Calcium: workouts, meals, community, streaks.
				</div>
				<h1 className={style.Headline}>
					Your next workout and meals, ready in one tap.
				</h1>
				<p className={style.Subline}>
					Follow a clear plan, keep your streak going, and stay consistent with people who show up.
				</p>
				<div className={style.HeroCTA}>
					<PrimaryButton
						text="Get my free plan"
						click={handleStart}
						width="280px"
					/>
					{!user && (
						<div className={style.HeroReturning}>
							<span>Already have an account?</span>
							<Link
								to={`/login?redirectTo=${encodeURIComponent('/')}`}
								className={style.HeroReturningLink}
							>
								Log in
							</Link>
						</div>
					)}
					<div className={style.Micro}>
						<span>~30 seconds. No planning. No second-guessing.</span>
					</div>
				</div>
				<div className={style.FeedbackWrap} aria-label="User feedback">
					<div className={style.FeedbackTrack}>
						{[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
							<div key={i} className={style.FeedbackCard}>
								<p className={style.FeedbackQuote}>{t.quote}</p>
								<span className={style.FeedbackAttribution}>
									{t.name}
								</span>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ===== Phone Swiper ===== */}
			<section
				className={style.Showcase}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
				onTouchEnd={handleTouchEnd}
				aria-label="Feature showcase"
				aria-roledescription="carousel"
			>
				<div className={style.ShowcaseIntro}>
					<span className={style.ShowcaseEyebrow}>How Calcium works</span>
					<p className={style.ShowcaseText}>
						Start with AI or create your own plan from scratch, then adapt workouts,
						meals, and routines anytime as your goals, schedule, and energy change.
					</p>
				</div>
				<div className={style.PhoneWrap}>
					<div className={style.PhoneGlow} aria-hidden="true" />
					<div className={style.Phone}>
						<div className={style.PhoneIsland} />
						<div className={style.PhoneScreen}>
							{SLIDES.map((slide, i) => (
								<div
									key={i}
									className={`${style.Screen} ${i === activeSlide ? style.ScreenActive : ''}`}
									role="tabpanel"
									aria-hidden={i !== activeSlide}
								>
									{slide.image && !imageErrors[i] ? (
										<img
											src={slide.image}
											alt={slide.title}
											className={style.ScreenImg}
											onError={() => handleImageError(i)}
										/>
									) : (
										<div className={style.Placeholder}>
											<div className={style.PlaceholderIcon}>{slide.icon}</div>
											<span className={style.PlaceholderTitle}>{slide.title}</span>
											<span className={style.PlaceholderFile}>
												{slide.placeholderLabel}
											</span>
										</div>
									)}
								</div>
							))}
						</div>
						<div className={style.PhoneBar} />
					</div>
				</div>

				<div className={style.SlideControls}>
					<div className={style.Dots} role="tablist" aria-label="Slides">
						{SLIDES.map((_, i) => (
							<button
								key={i}
								role="tab"
								aria-selected={i === activeSlide}
								aria-label={`Slide ${i + 1}: ${SLIDES[i].title}`}
								className={`${style.Dot} ${i === activeSlide ? style.DotActive : ''}`}
								onClick={() => goToSlide(i)}
							/>
						))}
					</div>

					<div className={style.SlideContent}>
						{SLIDES.map((slide, i) => (
							<div
								key={i}
								className={`${style.SlideInfo} ${i === activeSlide ? style.SlideInfoActive : ''}`}
							>
								<div className={style.SlideTag}>
									<span className={style.SlideTagIcon}>{slide.icon}</span>
									{slide.tag}
								</div>
								<h2 className={style.SlideTitle}>{slide.title}</h2>
								<p className={style.SlideDesc}>{slide.description}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* ===== Bottom ===== */}
			<footer className={style.Footer}>
				<div className={style.FooterBottom}>
					<div className={style.FooterLinks}>
						<Link to="/terms" className={style.FooterLink}>Terms</Link>
						<Link to="/privacy" className={style.FooterLink}>Privacy</Link>
						<a href="mailto:hello@calcium.fitness" className={style.FooterLink}>Contact</a>
					</div>
					<a
						href="https://www.microsoft.com/startups"
						target="_blank"
						rel="noopener noreferrer"
						className={style.FooterBadgeLink}
						aria-label="Microsoft for Startups"
					>
						<img src={msBadge} alt="Microsoft for Startups badge" className={style.FooterBadge} />
					</a>
				</div>
			</footer>
		</div>
	);
};

export default LandingPage;
